/**
 * The HUD's fast-changing numbers, written straight into DOM nodes: the race timer, the
 * level progress bar, the avatars on the race strip and the boost timer rings. Components
 * register their nodes through ref callbacks; the controller calls the setters a few times a
 * second. Every setter writes only what changed, so a quiet HUD costs no style work.
 */
import type { ActiveBoost, BoostType, PlayerId } from '@cube-rush/shared';
import { formatRaceTimer, formatTimerLeft } from '../raceClock';

/** Racers closer than this on the strip (fraction of the race) stack in lanes. */
const LANE_GAP = 0.045;
const LANE_SHIFT_PX = 9;
/**
 * Lanes on the track, then above and below it (in LANE_SHIFT_PX): a crowd at the start stays
 * inside the top bar's box, not under a tablet's status bar above it.
 */
const LANES = [0, -1, 1] as const;
/**
 * When the track of a race against the clock grows, the avatars ease to their new spots for
 * this long (a `left` transition only then: it lays the strip out on every frame it runs).
 */
const RESCALE_MS = 450;
const RESCALE_TRANSITION = `left ${RESCALE_MS}ms ease-out, transform 0.2s ease-out`;

export class HudDom {
  /** The top bar: views under it keep their content below it. */
  topBar: HTMLElement | null = null;
  /**
   * Controls along the bottom edge (boost buttons, spectator bar, the compact finish card):
   * the views keep the floor above them where they can.
   */
  readonly bottomControls = new Map<string, HTMLElement>();
  timer: HTMLElement | null = null;
  levelBar: HTMLElement | null = null;
  readonly markers = new Map<PlayerId, HTMLElement>();
  readonly boostTimers = new Map<BoostType, HTMLElement>();
  private shownTenths = -1;
  /** What the time-left timer shows: whole seconds, or tenths as negative numbers. */
  private shownLeft = Number.NaN;
  private shownBar = -1;
  private rescaleUntil = Number.NEGATIVE_INFINITY;

  /** Ref callback for a control along the bottom edge (see bottomControls). */
  bottomControl(key: string, element: HTMLElement | null): void {
    if (element) this.bottomControls.set(key, element);
    else this.bottomControls.delete(key);
  }

  /** Ref callback for a strip marker. */
  marker(id: PlayerId, element: HTMLElement | null): void {
    if (element) this.markers.set(id, element);
    else this.markers.delete(id);
  }

  /** Ref callback for the timer ring of a running boost. */
  boostTimer(type: BoostType, element: HTMLElement | null): void {
    if (element) this.boostTimers.set(type, element);
    else this.boostTimers.delete(type);
  }

  /** Race time in ms (shown as m:ss.d). */
  setTimer(ms: number): void {
    const tenths = Math.floor(Math.max(0, ms) / 100);
    if (tenths === this.shownTenths || !this.timer) return;
    this.shownTenths = tenths;
    this.timer.textContent = formatRaceTimer(ms);
  }

  /** Time left of a race against the clock in ms (m:ss, tenths in the last seconds). */
  setTimeLeft(ms: number): void {
    const tenths = Math.max(0, Math.ceil(ms / 100));
    const shown = tenths >= 100 ? Math.ceil(ms / 1000) : -tenths;
    if (shown === this.shownLeft || !this.timer) return;
    this.shownLeft = shown;
    this.timer.textContent = formatTimerLeft(ms);
  }

  /** The strip's track just got longer (`now`: engine clock): the avatars ease over. */
  rescaleStrip(now: number): void {
    this.rescaleUntil = now + RESCALE_MS;
  }

  /** Progress through the current level, 0..1. */
  setLevelBar(fraction: number): void {
    const bar = Math.round(Math.min(1, Math.max(0, fraction)) * 200) / 200;
    if (bar === this.shownBar || !this.levelBar) return;
    this.shownBar = bar;
    this.levelBar.style.transform = `scaleX(${bar})`;
  }

  /**
   * Moves the strip avatars to their overall progress; close ones stack in lanes. `now`
   * (engine clock) tells whether a re-scaled track is still easing.
   */
  placeMarkers(progressOf: (id: PlayerId) => number, now = 0): void {
    if (this.markers.size === 0) return;
    const transition = now < this.rescaleUntil ? RESCALE_TRANSITION : '';
    const placed: Array<{ id: PlayerId; p: number }> = [];
    for (const id of this.markers.keys()) placed.push({ id, p: progressOf(id) });
    placed.sort((a, b) => a.p - b.p);
    let lane = 0;
    let previous = Number.NEGATIVE_INFINITY;
    for (const { id, p } of placed) {
      lane = p - previous < LANE_GAP ? lane + 1 : 0;
      previous = p;
      const element = this.markers.get(id)!;
      // Before `left`: the change that re-scaled the track is the one to ease.
      if (element.style.transition !== transition) element.style.transition = transition;
      const left = `${(p * 100).toFixed(1)}%`;
      if (element.style.left !== left) element.style.left = left;
      const shift = `translate(-50%, ${LANES[lane % LANES.length]! * LANE_SHIFT_PX}px)`;
      if (element.style.transform !== shift) element.style.transform = shift;
    }
  }

  /** Empties the rings of the running boosts as their time runs out. */
  setBoostTimers(active: ReadonlyArray<ActiveBoost>): void {
    if (this.boostTimers.size === 0) return;
    for (const boost of active) {
      const element = this.boostTimers.get(boost.type);
      if (!element) continue;
      const left = `${Math.round((boost.remaining / boost.duration) * 100)}`;
      if (element.dataset.left === left) continue;
      element.dataset.left = left;
      element.style.setProperty('--left', `${left}%`);
    }
  }
}
